// src/pages/personal/analytics/DashboardSection/components/SalesChannels/SalesChannels.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout';
import { COLORS } from '@/theme';
import { ChannelsChart } from './ChannelsChart';
import { DataList, DataListColumnDef } from '@/components/ui/DataList';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatPercent, formatNumber } from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import styles from '../../DashboardSection.module.css';
import { PlatformItem } from '../../../types/types';

interface SalesChannelsProps {
  data: PlatformItem[];
  isLoading?: boolean;
}

// Тип для ключей в platformColors объекте
type PlatformColorKey = 'whatsapp' | 'instagram' | 'telegram' | 'website' | 'default';

const SalesChannels: React.FC<SalesChannelsProps> = ({ data, isLoading = false }) => {
  const { t, i18n } = useTranslation('dashboard');
  const { formatPrice } = useBusinessCurrency();
  
  // Безопасное определение локали
  const currentLocale = i18n.language.toUpperCase() === 'RU' 
    ? LOCALES.RU 
    : i18n.language.toUpperCase() === 'EN' 
      ? LOCALES.EN 
      : LOCALES.RU;
  
  // Цвета для различных платформ из системы тем
  const platformColors = COLORS.sourceChartColors;
  
  // Функция для получения цвета платформы с безопасной типизацией
  const getPlatformColor = (platform: string): string => {
    const platformKey = platform.toLowerCase() as PlatformColorKey;
    return platformKey in platformColors ? platformColors[platformKey] : platformColors.default;
  };
  
  // Подготовка данных для круговой диаграммы
  const chartData = data.map(item => ({
    name: t(`platforms.${item.platform}`, { defaultValue: item.platform }),
    value: item.count,
    percentage: item.percentage,
    color: getPlatformColor(item.platform),
    total: item.total,
    // Форматируем процент с помощью утилиты
    formattedPercentage: formatPercent(item.percentage, 1, true, currentLocale)
  }));
  
  // Определение колонок для списка данных
  const columns: DataListColumnDef<PlatformItem>[] = [
    {
      key: 'platform',
      title: t('dashboard:sales.channels.channel'),
      width: 'lg',
      render: (item: PlatformItem) => (
        t(`platforms.${item.platform}`, { defaultValue: item.platform })
      )
    },
    {
      key: 'count',
      title: t('dashboard:sales.channels.orders'),
      width: 'sm',
      textAlign: 'right',
      // Используем форматер чисел
      render: (item: PlatformItem) => formatNumber(item.count, 0, true, currentLocale)
    },
    {
      key: 'percentage',
      title: t('dashboard:sales.channels.percentage'),
      width: 'sm',
      textAlign: 'right',
      // Используем форматер процентов
      render: (item: PlatformItem) => formatPercent(item.percentage, 1, true, currentLocale)
    },
    {
      key: 'total',
      title: t('dashboard:sales.channels.revenue'),
      width: 'sm',
      textAlign: 'right',
      render: (item: PlatformItem) => formatPrice(item.total)
    }
  ];
  
  return (
    <Card padding="none" shadow="sm">
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('dashboard:sales.channels.title')}</h3>
        <p className={styles.cardSubtitle}>{t('dashboard:sales.channels.orders')}</p>
      </div>
      <div className={styles.cardContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <p className={styles.loadingText}>{t('loading')}</p>
          </div>
        ) : chartData.length > 0 ? (
          <div className={styles.channelsContainer}>
            {/* Используем унифицированный класс chartContainer для графика */}
            <div className={styles.chartContainer}>
              <ChannelsChart data={chartData} />
            </div>
            
            <div className={styles.channelsData}>
              <DataList 
                data={data}
                columns={columns}
                noDataText={t('no_data')}
                compact={true}
                getItemColor={(item) => getPlatformColor(item.platform)}
              />
            </div>
          </div>
        ) : (
          <div className={styles.noDataContainer}>
            <p>{t('no_data')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SalesChannels;
